// แม่แบบวงจรชีวิต (docs/PLAN.md ข้อ 3.1, 3.3, 3.5)
// ทุกชีวิตยาวราว 20–40 turn ส่วนเวลาจริงต่อ turn คำนวณจากอายุขัยของชนิดนั้น

import type { ArchetypeId } from "../data/groups";
import type { Rng } from "./rng";
import type { SpeciesInfo, StageDef } from "./types";

export interface ArchetypeDef {
  id: ArchetypeId;
  survivalToAdult: number; // เป้าหมายในเกม % ที่รอดถึงตัวเต็มวัย
  youngHazardScale?: number; // ปรับความเสี่ยงช่วงวัยเด็กให้ผลจากตัวจำลองตรงเป้า (npm run simulate)
  adultHazard: number; // โอกาสตายต่อ turn จากความเสี่ยงพื้นฐานในวัยเจริญพันธุ์
  oldHazard: number; // ต่อ turn ในวัยชรา หรือ turn ท้ายๆ ของชีวิต
  earlyDeathFact: string; // ข้อเท็จจริงที่แสดงเมื่อตายก่อนโต
  stages: (species: SpeciesInfo) => readonly StageDef[];
  startFlags?: (species: SpeciesInfo, rng: Rng) => string[]; // ใส่ flag ตอนเกิด เช่น บทบาทในรังของแมลงสังคม
}

const egg = (turns: number, lifeShare: number, enterText?: string): StageDef => ({
  id: "egg", nameTh: "ไข่", turns, lifeShare, tag: "dormant", feeds: false, enterText,
});
const adult = (turns: number, lifeShare: number, enterText?: string): StageDef => ({
  id: "adult", nameTh: "ตัวเต็มวัย", turns, lifeShare, tag: "adult", feeds: true, enterText,
});
const elder = (turns: number, lifeShare: number): StageDef => ({
  id: "elder", nameTh: "วัยชรา", turns, lifeShare, tag: "old", feeds: true,
  enterText: "ปีแล้วปีเล่าผ่านไป ร่างกายของคุณเริ่มแก่ตัวลง",
});

const MAMMAL_STAGES: readonly StageDef[] = [
  { id: "newborn", nameTh: "แรกเกิด", turns: 2, lifeShare: 0.01, tag: "dependent", feeds: true },
  { id: "nursing", nameTh: "วัยกินนม", turns: 4, lifeShare: 0.05, tag: "dependent", feeds: true, enterText: "คุณเริ่มลืมตาและคลานไปมารอบตัวแม่" },
  { id: "juvenile", nameTh: "วัยรุ่น", turns: 5, lifeShare: 0.14, tag: "young", feeds: true, enterText: "คุณตัวโตขึ้นและเริ่มออกสำรวจโลกด้วยตัวเอง" },
  adult(12, 0.6, "คุณโตเต็มวัยแล้ว พร้อมจะมีครอบครัวของตัวเอง"),
  elder(4, 0.2),
];

const BIRD_STAGES: readonly StageDef[] = [
  egg(2, 0.01),
  { id: "nestling", nameTh: "ลูกนกในรัง", turns: 3, lifeShare: 0.02, tag: "dependent", feeds: true, enterText: "คุณเจาะเปลือกไข่ออกมาสำเร็จ! ตัวยังไม่มีขน ตายังไม่เปิด" },
  { id: "fledgling", nameTh: "วัยหัดบิน", turns: 3, lifeShare: 0.05, tag: "young", feeds: true, enterText: "ขนของคุณขึ้นเต็มตัวแล้ว รังเริ่มคับแคบ" },
  adult(14, 0.72, "คุณโตเต็มวัยแล้ว ขนสวยงามพร้อมสำหรับฤดูผสมพันธุ์"),
  elder(4, 0.2),
];

const REPTILE_STAGES: readonly StageDef[] = [
  egg(3, 0.02),
  { id: "hatchling", nameTh: "ลูกแรกฟัก", turns: 3, lifeShare: 0.05, tag: "young", feeds: true, enterText: "คุณใช้ฟันพิเศษที่ปลายปากเจาะเปลือกไข่ออกมาสู่โลกภายนอก" },
  { id: "juvenile", nameTh: "วัยรุ่น", turns: 5, lifeShare: 0.18, tag: "young", feeds: true, enterText: "คุณตัวโตขึ้น นักล่าหลายชนิดเริ่มมองข้ามคุณไป" },
  adult(12, 0.55, "คุณโตเต็มวัยแล้ว"),
  elder(4, 0.2),
];

const AMPHIBIAN_STAGES = (species: SpeciesInfo): readonly StageDef[] => {
  const larva = species.subgroupId === "amphibian.frog" ? "ลูกอ๊อด" : "ตัวอ่อน";
  return [
    egg(2, 0.01),
    { id: "tadpole", nameTh: larva, turns: 5, lifeShare: 0.06, tag: "young", feeds: true, enterText: `คุณฟักออกจากไข่เป็น${larva} หายใจด้วยเหงือกในน้ำ` },
    { id: "metamorph", nameTh: "ระยะเปลี่ยนรูป", turns: 2, lifeShare: 0.03, tag: "young", feeds: false, enterText: "ร่างกายของคุณกำลังเปลี่ยนไปอย่างมาก ขางอก เหงือกหายไป" },
    adult(14, 0.7, "คุณขึ้นมาใช้ชีวิตบนบกได้แล้ว"),
    elder(3, 0.2),
  ];
};

const FISH_STAGES = (species: SpeciesInfo): readonly StageDef[] => {
  const live = species.traits.reproduction === "live";
  return [
    live
      ? { id: "newborn", nameTh: "ลูกแรกเกิด", turns: 2, lifeShare: 0.01, tag: "young", feeds: true }
      : egg(2, 0.01),
    { id: "larva", nameTh: "ลูกปลาวัยอ่อน", turns: 4, lifeShare: 0.04, tag: "young", feeds: true, enterText: live ? undefined : "คุณฟักออกจากไข่ ตัวใสจนแทบมองไม่เห็น" },
    { id: "juvenile", nameTh: "ปลาวัยรุ่น", turns: 5, lifeShare: 0.15, tag: "young", feeds: true, enterText: "เกล็ดและครีบของคุณเริ่มชัดเจน หน้าตาเหมือนพ่อแม่มากขึ้น" },
    adult(15, 0.8, "คุณโตเต็มวัยแล้ว"),
  ];
};

const INSECT_STAGES = (species: SpeciesInfo): readonly StageDef[] =>
  species.traits.metamorphosis === "complete"
    ? [
        egg(2, 0.02),
        { id: "larva", nameTh: "หนอน", turns: 8, lifeShare: 0.5, tag: "young", feeds: true, enterText: "คุณฟักออกจากไข่เป็นหนอนตัวจิ๋ว และหิวมาก" },
        { id: "pupa", nameTh: "ดักแด้", turns: 2, lifeShare: 0.15, tag: "dormant", feeds: false, enterText: "คุณสร้างดักแด้หุ้มตัวเอง ร่างกายข้างในกำลังเปลี่ยนแปลงทั้งหมด" },
        adult(10, 0.33, "คุณออกจากดักแด้ กางปีกที่ยังเปียกชื้นเป็นครั้งแรก"),
      ]
    : [
        egg(2, 0.05),
        { id: "nymph", nameTh: "ตัวอ่อน", turns: 10, lifeShare: 0.6, tag: "young", feeds: true, enterText: "คุณฟักออกมาเป็นตัวอ่อนหน้าตาเหมือนพ่อแม่ แต่ยังไม่มีปีก" },
        adult(10, 0.35, "คุณลอกคราบครั้งสุดท้าย ปีกกางออกเต็มที่แล้ว"),
      ];

// ---------- เฟส 3 ----------

// มด ปลวก ผึ้งพันธุ์ ตัวอ่อนมีพี่ๆ คอยป้อนอาหาร บทบาทในรังสุ่มตอนเกิด (ดู startFlags)
const SOCIAL_INSECT_STAGES = (species: SpeciesInfo): readonly StageDef[] =>
  species.traits.metamorphosis === "complete"
    ? [
        egg(2, 0.02),
        { id: "larva", nameTh: "ตัวอ่อนในรัง", turns: 4, lifeShare: 0.08, tag: "dependent", feeds: true, enterText: "คุณฟักเป็นตัวอ่อนไร้ขา พี่ๆ ในรังคอยป้อนอาหารให้ไม่หยุด" },
        { id: "pupa", nameTh: "ดักแด้", turns: 2, lifeShare: 0.05, tag: "dormant", feeds: false, enterText: "คุณเข้าดักแด้ ร่างกายกำลังเปลี่ยนเป็นตัวเต็มวัย" },
        adult(14, 0.85, "คุณออกจากดักแด้ และรู้ทันทีว่าหน้าที่ของคุณในรังคืออะไร"),
      ]
    : [
        egg(2, 0.03),
        { id: "nymph", nameTh: "ตัวอ่อนในรัง", turns: 6, lifeShare: 0.12, tag: "dependent", feeds: true, enterText: "คุณฟักออกมาในรังมืดๆ พี่ๆ ในรังคอยป้อนอาหารให้" },
        adult(14, 0.85, "คุณลอกคราบเป็นตัวเต็มวัย และรู้ทันทีว่าหน้าที่ของคุณในรังคืออะไร"),
      ];

const ARACHNID_STAGES = (species: SpeciesInfo): readonly StageDef[] => {
  if (species.subgroupId === "arachnid.scorpion") {
    return [
      { id: "newborn", nameTh: "ลูกบนหลังแม่", turns: 3, lifeShare: 0.03, tag: "dependent", feeds: false, enterText: "คุณเกิดมาตัวขาวนวล แล้วปีนขึ้นไปเกาะบนหลังแม่พร้อมพี่น้องอีกหลายสิบตัว" },
      { id: "juvenile", nameTh: "วัยรุ่น", turns: 6, lifeShare: 0.27, tag: "young", feeds: true, enterText: "คุณลอกคราบครั้งแรก แล้วลงจากหลังแม่ไปใช้ชีวิตเอง" },
      adult(12, 0.6, "คุณลอกคราบครั้งสุดท้าย ก้ามและเหล็กในแข็งแรงเต็มที่"),
      elder(3, 0.1),
    ];
  }
  if (species.subgroupId === "arachnid.mite_tick") {
    return [
      egg(2, 0.05),
      { id: "larva", nameTh: "ตัวอ่อนหกขา", turns: 3, lifeShare: 0.15, tag: "young", feeds: true, enterText: "คุณฟักออกมาเป็นตัวอ่อนที่มีขาแค่หกขา" },
      { id: "nymph", nameTh: "ตัวอ่อนแปดขา", turns: 4, lifeShare: 0.3, tag: "young", feeds: true, enterText: "คุณลอกคราบ และมีขาครบแปดขาแล้ว" },
      adult(10, 0.5, "คุณลอกคราบครั้งสุดท้าย โตเต็มวัยแล้ว"),
    ];
  }
  const young = species.subgroupId === "arachnid.spider" ? "ลูกแมงมุม" : "ตัวอ่อน";
  return [
    egg(2, 0.05),
    { id: "spiderling", nameTh: young, turns: 6, lifeShare: 0.35, tag: "young", feeds: true, enterText: `คุณกับพี่น้องหลายร้อยตัวคลานออกจากถุงไข่ คุณเป็น${young}ตัวจิ๋ว` },
    adult(12, 0.5, "คุณลอกคราบครั้งสุดท้าย โตเต็มวัยแล้ว"),
    elder(3, 0.1),
  ];
};

const CRUSTACEAN_STAGES: readonly StageDef[] = [
  egg(2, 0.02),
  { id: "larva", nameTh: "ตัวอ่อนแพลงก์ตอน", turns: 4, lifeShare: 0.08, tag: "young", feeds: true, enterText: "คุณฟักออกจากไข่เป็นตัวอ่อนตัวจิ๋ว ล่องลอยไปกับกระแสน้ำ หน้าตาไม่เหมือนพ่อแม่เลย" },
  { id: "juvenile", nameTh: "วัยรุ่น", turns: 5, lifeShare: 0.2, tag: "young", feeds: true, enterText: "คุณลอกคราบหลายครั้งจนหน้าตาเหมือนพ่อแม่ แล้วลงไปใช้ชีวิตที่พื้นน้ำ" },
  adult(12, 0.6, "คุณโตเต็มวัยแล้ว"),
  elder(3, 0.1),
];

const MYRIAPOD_STAGES = (species: SpeciesInfo): readonly StageDef[] => [
  egg(2, 0.03),
  {
    id: "juvenile", nameTh: "ตัวอ่อน", turns: 8, lifeShare: 0.37, tag: "young", feeds: true,
    enterText: species.subgroupId === "myriapod.millipede"
      ? "คุณฟักออกมามีขาแค่ไม่กี่คู่ ทุกครั้งที่ลอกคราบจะมีปล้องและขาเพิ่มขึ้น"
      : "คุณฟักออกมาตัวจิ๋ว แม่ยังขดตัวเฝ้าคุณกับพี่น้องอยู่",
  },
  adult(12, 0.5, "คุณโตเต็มวัยแล้ว ขาครบทุกปล้อง"),
  elder(3, 0.1),
];

const MOLLUSC_STAGES = (species: SpeciesInfo): readonly StageDef[] => {
  if (species.subgroupId === "mollusc.cephalopod") {
    // หมึกส่วนใหญ่มีลูกได้ครั้งเดียวแล้วตาย จึงไม่มีวัยชรา (ดูเหตุการณ์ ceph_guard_eggs)
    return [
      egg(3, 0.1),
      { id: "paralarva", nameTh: "ลูกหมึกวัยอ่อน", turns: 5, lifeShare: 0.25, tag: "young", feeds: true, enterText: "คุณฟักออกจากไข่ตัวใสจิ๋ว ล่องลอยอยู่ในน้ำพร้อมพี่น้องนับพัน" },
      adult(12, 0.65, "คุณโตเต็มวัยแล้ว เปลี่ยนสีผิวได้ในพริบตา"),
    ];
  }
  if (species.subgroupId === "mollusc.gastropod") {
    return [
      egg(2, 0.03),
      { id: "juvenile", nameTh: "ลูกหอย", turns: 6, lifeShare: 0.27, tag: "young", feeds: true, enterText: "คุณฟักออกมาพร้อมเปลือกบางๆ ติดตัว" },
      adult(12, 0.6, "เปลือกของคุณหนาและแข็งแรงแล้ว"),
      elder(3, 0.1),
    ];
  }
  return [
    egg(2, 0.01),
    { id: "larva", nameTh: "ตัวอ่อนล่องลอย", turns: 3, lifeShare: 0.02, tag: "young", feeds: true, enterText: "คุณเป็นตัวอ่อนที่มีขนเล็กๆ ไว้ว่ายน้ำ ล่องลอยไปกับแพลงก์ตอน" },
    { id: "settle", nameTh: "หาที่ลงเกาะ", turns: 2, lifeShare: 0.07, tag: "young", feeds: true, enterText: "ถึงเวลาเลือกที่ลงเกาะ ที่ที่คุณอาจต้องอยู่ไปตลอดชีวิต" },
    adult(13, 0.7, "คุณโตเต็มวัยแล้ว"),
    elder(3, 0.2),
  ];
};

const WORM_STAGES = (species: SpeciesInfo): readonly StageDef[] => [
  egg(2, 0.05),
  {
    id: "juvenile", nameTh: "ตัวอ่อน", turns: 7, lifeShare: 0.3, tag: "young", feeds: true,
    enterText: species.subgroupId === "worm.annelid" ? "คุณออกจากปลอกไข่ ตัวเล็กเท่าเส้นด้าย" : "คุณฟักออกจากไข่ ตัวเล็กจนมองด้วยตาเปล่าแทบไม่เห็น",
  },
  adult(13, 0.55, "คุณโตเต็มวัยแล้ว"),
  elder(3, 0.1),
];

const SESSILE_STAGES = (species: SpeciesInfo): readonly StageDef[] => {
  const planula: StageDef = {
    id: "planula", nameTh: "ตัวอ่อนพลานูลา", turns: 3, lifeShare: 0.005, tag: "young", feeds: false,
    enterText: "คุณเริ่มต้นชีวิตเป็นตัวอ่อนจิ๋วของ{name} ล่องลอยไปกับกระแสน้ำ",
  };
  if (species.subgroupId === "cnidarian.jellyfish") {
    return [
      planula,
      { id: "polyp", nameTh: "โพลิป", turns: 4, lifeShare: 0.3, tag: "young", feeds: true, enterText: "คุณลงเกาะใต้โขดหิน กลายเป็นโพลิปตัวเล็กๆ หน้าตาเหมือนดอกไม้" },
      { id: "ephyra", nameTh: "ลูกแมงกะพรุน", turns: 3, lifeShare: 0.1, tag: "young", feeds: true, enterText: "โพลิปของคุณแตกออกเป็นแผ่นซ้อนกัน แล้วคุณก็หลุดออกมาเป็นแมงกะพรุนตัวจิ๋ว" },
      adult(10, 0.595, "ร่มของคุณกางเต็มที่ หนวดยาวสลวย คุณเป็นแมงกะพรุนโตเต็มวัยแล้ว"),
    ];
  }
  return [
    planula,
    { id: "polyp", nameTh: "โพลิปเดี่ยว", turns: 5, lifeShare: 0.05, tag: "young", feeds: true, enterText: "คุณลงเกาะบนหินแข็ง และเริ่มสร้างโครงหินปูนรอบตัว" },
    adult(14, 0.75, "คุณแตกหน่อเป็นโพลิปนับร้อย กลายเป็นอาณานิคมที่เติบโตขึ้นทุกปี"),
    elder(3, 0.195),
  ];
};

const ECHINODERM_STAGES: readonly StageDef[] = [
  egg(2, 0.005),
  { id: "larva", nameTh: "ตัวอ่อนแพลงก์ตอน", turns: 4, lifeShare: 0.03, tag: "young", feeds: true, enterText: "คุณเป็นตัวอ่อนใสๆ ล่องลอยในทะเล หน้าตาไม่เหมือนพ่อแม่เลยสักนิด" },
  { id: "juvenile", nameTh: "วัยรุ่น", turns: 5, lifeShare: 0.165, tag: "young", feeds: true, enterText: "คุณจมลงสู่พื้นทะเล และร่างกายเปลี่ยนเป็นรูปทรงห้าแฉก" },
  adult(12, 0.6, "คุณโตเต็มวัยแล้ว"),
  elder(3, 0.2),
];

export const ARCHETYPES: Partial<Record<ArchetypeId, ArchetypeDef>> = {
  MAMMAL: {
    id: "MAMMAL",
    youngHazardScale: 0.75,
    survivalToAdult: 0.7,
    adultHazard: 0.012,
    oldHazard: 0.12,
    earlyDeathFact: "ในธรรมชาติ ลูกสัตว์เลี้ยงลูกด้วยนมจำนวนมากไม่รอดถึงวัยเจริญพันธุ์ แม้จะมีแม่คอยดูแล",
    stages: () => MAMMAL_STAGES,
  },
  BIRD: {
    id: "BIRD",
    survivalToAdult: 0.6,
    youngHazardScale: 0.9,
    adultHazard: 0.015,
    oldHazard: 0.12,
    earlyDeathFact: "ลูกนกจำนวนมากไม่รอดถึงปีแรก ช่วงหัดบินเป็นช่วงที่อันตรายที่สุดช่วงหนึ่ง",
    stages: () => BIRD_STAGES,
  },
  REPTILE: {
    id: "REPTILE",
    youngHazardScale: 0.7,
    survivalToAdult: 0.55,
    adultHazard: 0.01,
    oldHazard: 0.1,
    earlyDeathFact: "ลูกสัตว์เลื้อยคลานส่วนใหญ่ต้องเอาตัวรอดเองตั้งแต่ฟัก และมีเพียงส่วนน้อยที่รอดจนโต",
    stages: () => REPTILE_STAGES,
  },
  AMPHIBIAN: {
    id: "AMPHIBIAN",
    survivalToAdult: 0.45,
    youngHazardScale: 1.6,
    adultHazard: 0.015,
    oldHazard: 0.12,
    earlyDeathFact: "สัตว์สะเทินน้ำสะเทินบกวางไข่ทีละมาก เพราะไข่และลูกอ๊อดส่วนใหญ่ไม่รอดจนขึ้นบก",
    stages: AMPHIBIAN_STAGES,
  },
  FISH: {
    id: "FISH",
    survivalToAdult: 0.45,
    youngHazardScale: 1.5,
    adultHazard: 0.015,
    oldHazard: 0.1,
    earlyDeathFact: "ปลาหลายชนิดวางไข่ครั้งละหลายพันฟอง แต่มีเพียงไม่กี่ตัวที่รอดจนโต",
    stages: FISH_STAGES,
  },
  INSECT: {
    id: "INSECT",
    survivalToAdult: 0.5,
    adultHazard: 0.02,
    oldHazard: 0.15,
    earlyDeathFact: "แมลงวางไข่ครั้งละมาก เพราะไข่และตัวอ่อนส่วนใหญ่กลายเป็นอาหารของสัตว์อื่นก่อนจะโต",
    stages: INSECT_STAGES,
  },
  SOCIAL_INSECT: {
    id: "SOCIAL_INSECT",
    survivalToAdult: 0.75,
    youngHazardScale: 0.5,
    adultHazard: 0.025,
    oldHazard: 0.15,
    earlyDeathFact: "ตัวอ่อนในรังมีพี่ๆ คอยดูแล จึงรอดมากกว่าแมลงทั่วไป แต่โรคและผู้บุกรุกก็กวาดล้างได้ทั้งรัง",
    stages: SOCIAL_INSECT_STAGES,
    // ในรังจริง นางพญามีแค่ตัวเดียวต่อสมาชิกนับพัน แต่ในเกมเพิ่มโอกาสให้ได้ลองเล่นเป็นนางพญาบ้าง
    startFlags: (_species, rng) => [rng.chance(0.15) ? "role_queen" : "role_worker"],
  },
  ARACHNID: {
    id: "ARACHNID",
    survivalToAdult: 0.5,
    youngHazardScale: 1.25,
    adultHazard: 0.02,
    oldHazard: 0.15,
    earlyDeathFact: "ลูกแมงมุมนับร้อยฟักออกมาพร้อมกัน แต่ส่วนใหญ่ถูกกินหรือหาอาหารไม่ได้ก่อนจะโต",
    stages: ARACHNID_STAGES,
  },
  CRUSTACEAN: {
    id: "CRUSTACEAN",
    survivalToAdult: 0.45,
    youngHazardScale: 1.3,
    adultHazard: 0.015,
    oldHazard: 0.12,
    earlyDeathFact: "ตัวอ่อนของกุ้งปูล่องลอยเป็นแพลงก์ตอน และเป็นอาหารสำคัญของสัตว์ทะเลอีกหลายชนิด จึงรอดน้อยมาก",
    stages: () => CRUSTACEAN_STAGES,
  },
  MYRIAPOD: {
    id: "MYRIAPOD",
    survivalToAdult: 0.55,
    youngHazardScale: 1.1,
    adultHazard: 0.012,
    oldHazard: 0.1,
    earlyDeathFact: "ตะขาบและกิ้งกือตัวอ่อนลำตัวนิ่ม ต้องลอกคราบหลายครั้งกว่าจะแข็งแรง ช่วงลอกคราบจึงอันตรายที่สุด",
    stages: MYRIAPOD_STAGES,
  },
  MOLLUSC: {
    id: "MOLLUSC",
    survivalToAdult: 0.45,
    youngHazardScale: 1.3,
    adultHazard: 0.012,
    oldHazard: 0.12,
    earlyDeathFact: "หอยและหมึกหลายชนิดปล่อยไข่ทีละหลายพันถึงหลายล้านฟอง เพราะตัวอ่อนแทบทั้งหมดไม่รอด",
    stages: MOLLUSC_STAGES,
  },
  WORM: {
    id: "WORM",
    survivalToAdult: 0.5,
    adultHazard: 0.015,
    oldHazard: 0.12,
    earlyDeathFact: "หนอนตัวอ่อนตัวเล็กมาก และเป็นอาหารของสัตว์ในดินและในน้ำเกือบทุกชนิด",
    stages: WORM_STAGES,
  },
  SESSILE: {
    id: "SESSILE",
    survivalToAdult: 0.45,
    youngHazardScale: 1.5,
    adultHazard: 0.01,
    oldHazard: 0.1,
    earlyDeathFact: "ตัวอ่อนปะการังและแมงกะพรุนส่วนใหญ่ถูกกินหรือหาที่ลงเกาะไม่ได้ ก่อนจะเติบโต",
    stages: SESSILE_STAGES,
  },
  ECHINODERM: {
    id: "ECHINODERM",
    survivalToAdult: 0.45,
    youngHazardScale: 1.15,
    adultHazard: 0.01,
    oldHazard: 0.1,
    earlyDeathFact: "ดาวทะเลและเม่นทะเลปล่อยไข่ทีละมหาศาล ตัวอ่อนส่วนใหญ่กลายเป็นอาหารของแพลงก์ตอนกินเนื้อและปลา",
    stages: () => ECHINODERM_STAGES,
  },
};

export function archetypeOf(species: SpeciesInfo): ArchetypeDef {
  const def = ARCHETYPES[species.archetype];
  if (!def) throw new Error(`archetype ${species.archetype} ยังไม่เปิดให้เล่น`);
  return def;
}
